'use client';

import { useState, useEffect, useCallback } from 'react';
import { Icon } from '@/components/ui/AppUI';

type IconName = Parameters<typeof Icon>[0]['name'];
type Tone = 'danger' | 'neutral' | 'positive';

interface ConfirmOptions {
  icon?:             IconName;
  title:             string;
  subtitle?:         string;
  confirmLabel?:     string;
  cancelLabel?:      string;
  tone?:             Tone;
  dismissOnBackdrop?: boolean;
}

interface QueueItem extends ConfirmOptions {
  id:      string;
  resolve: (value: boolean) => void;
}

// ── Global queue — imperative API ──────────────────────────────
let mountedSetter: ((fn: (prev: QueueItem[]) => QueueItem[]) => void) | null = null;
const pending: QueueItem[] = [];

export function confirm(opts: ConfirmOptions): Promise<boolean> {
  return new Promise(resolve => {
    const item: QueueItem = {
      id:               Math.random().toString(36).slice(2),
      tone:             opts.tone ?? 'danger',
      confirmLabel:     opts.confirmLabel ?? 'Confirm',
      cancelLabel:      opts.cancelLabel  ?? 'Cancel',
      dismissOnBackdrop: opts.dismissOnBackdrop ?? (opts.tone !== 'danger'),
      resolve,
      ...opts,
    };
    if (mountedSetter) mountedSetter(prev => [...prev, item]);
    else pending.push(item);
  });
}

// ── Single dialog ──────────────────────────────────────────────
function ConfirmDialog({ item, onCancel, onConfirm }: {
  item:      QueueItem;
  onCancel:  () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
      if (e.key === 'Enter')  { e.preventDefault(); onConfirm(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onCancel, onConfirm]);

  const iconBg =
    item.tone === 'danger'   ? 'color-mix(in oklch, var(--accent) 18%, transparent)' :
    item.tone === 'positive' ? 'color-mix(in oklch, oklch(70% 0.18 150) 18%, transparent)' :
    'rgba(255,255,255,0.06)';

  const iconColor =
    item.tone === 'danger'   ? 'var(--accent)' :
    item.tone === 'positive' ? 'oklch(75% 0.18 150)' :
    'var(--text)';

  const confirmBg =
    item.tone === 'danger'   ? 'var(--accent)' :
    item.tone === 'positive' ? 'oklch(70% 0.18 150)' :
    'var(--text)';

  const confirmColor =
    item.tone === 'positive' ? '#06120B' :
    item.tone === 'neutral'  ? 'var(--bg)' :
    'white';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'grid', placeItems: 'center',
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        padding: 16,
        animation: 'scf-fade 180ms ease-out',
      }}
      onMouseDown={e => {
        if (e.target === e.currentTarget && item.dismissOnBackdrop) onCancel();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div style={{
        width: 'min(480px, calc(100vw - 32px))',
        background: 'var(--bg-elev)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-lg)',
        padding: '20px 20px 16px',
        boxShadow: '0 24px 80px rgba(0,0,0,.5), 0 8px 24px rgba(0,0,0,.3)',
        animation: 'scf-pop 240ms cubic-bezier(.2,.9,.3,1.2) both',
      }}>
        {/* head */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '4px 4px 18px' }}>
          {item.icon && (
            <div style={{
              width: 44, height: 44, borderRadius: 'var(--r-md)',
              display: 'grid', placeItems: 'center', flexShrink: 0,
              background: iconBg, color: iconColor,
            }}>
              <Icon name={item.icon} size={24} stroke="currentColor" />
            </div>
          )}
          <div style={{ paddingTop: 4, flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, lineHeight: 1.25, letterSpacing: '-.01em', color: 'var(--text)' }}>
              {item.title}
            </h2>
            {item.subtitle && (
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.4, color: 'var(--text-dim)' }}>
                {item.subtitle}
              </p>
            )}
          </div>
        </div>

        {/* actions */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 10 }}>
          <button
            onClick={onCancel}
            style={{
              height: 52, borderRadius: 999, fontSize: 15, fontWeight: 600,
              cursor: 'pointer', border: '1px solid var(--line)',
              background: 'transparent', color: 'var(--text)',
              transition: 'background .12s, border-color .12s',
              fontFamily: 'var(--font-sans)',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            {item.cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            autoFocus
            style={{
              height: 52, borderRadius: 999, fontSize: 15, fontWeight: 600,
              cursor: 'pointer', border: 'none',
              background: confirmBg, color: confirmColor,
              transition: 'opacity .12s, transform .12s',
              fontFamily: 'var(--font-sans)',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '.88'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
            onMouseDown={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.98)'; }}
            onMouseUp={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
          >
            {item.confirmLabel}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes scf-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes scf-pop  {
          from { transform: translateY(8px) scale(0.98); opacity: 0; }
          to   { transform: translateY(0)   scale(1);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// Stable wrapper so the arrow lambdas in the map don't invalidate the
// keydown handler on every parent render.
import { memo } from 'react';
const MemoDialog = memo(function MemoDialog({
  item, onClose,
}: {
  item: QueueItem;
  onClose: (item: QueueItem, value: boolean) => void;
}) {
  const cancel  = useCallback(() => onClose(item, false), [item, onClose]);
  const confirm = useCallback(() => onClose(item, true),  [item, onClose]);
  return <ConfirmDialog item={item} onCancel={cancel} onConfirm={confirm} />;
});

// ── Host — mount once near the root ────────────────────────────
export function ConfirmHost() {
  const [items, setItems] = useState<QueueItem[]>([]);

  useEffect(() => {
    mountedSetter = setItems;
    if (pending.length) setItems(prev => [...prev, ...pending.splice(0)]);
    return () => { mountedSetter = null; };
  }, []);

  const close = useCallback((item: QueueItem, value: boolean) => {
    item.resolve(value);
    setItems(prev => prev.filter(i => i.id !== item.id));
  }, []);

  return (
    <>
      {items.map(item => (
        <MemoDialog
          key={item.id}
          item={item}
          onClose={close}
        />
      ))}
    </>
  );
}
